import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, Plus, Edit, Trash2, Check, X, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SearchBar from '@/components/shared/SearchBar';
import { API, toast } from '@/App';
import { getAuthToken } from '@/utils/authSession';

const CountryManagement = () => {
  const [countries, setCountries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    currency: '',
    phone_prefix: '',
    is_active: true,
    cities: []
  });
  const [newCity, setNewCity] = useState({ code: '', name: '', districts: '' });

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/countries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCountries(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des pays');
    } finally {
      setLoading(false);
    }
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = () => {
    setEditingCountry(null);
    setFormData({
      code: '',
      name: '',
      currency: '',
      phone_prefix: '',
      is_active: true,
      cities: []
    });
    setShowDialog(true);
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setFormData(country);
    setShowDialog(true);
  };

  const handleAddCity = () => {
    if (!newCity.code || !newCity.name) {
      toast.error('Code et nom de la ville sont obligatoires');
      return;
    }

    const districts = newCity.districts
      .split(',')
      .map(d => d.trim())
      .filter(d => d);

    setFormData(prev => ({
      ...prev,
      cities: [...prev.cities, {
        code: newCity.code.toUpperCase(),
        name: newCity.name,
        districts
      }]
    }));

    setNewCity({ code: '', name: '', districts: '' });
  };

  const handleRemoveCity = (cityCode) => {
    setFormData(prev => ({
      ...prev,
      cities: prev.cities.filter(c => c.code !== cityCode)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.name || !formData.currency || !formData.phone_prefix) {
      toast.error('Tous les champs obligatoires doivent être remplis');
      return;
    }

    try {
      const token = getAuthToken();
      const data = {
        ...formData,
        code: formData.code.toUpperCase()
      };

      if (editingCountry) {
        await axios.patch(`${API}/admin/countries/${editingCountry.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pays modifié avec succès');
      } else {
        await axios.post(`${API}/admin/countries`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pays créé avec succès');
      }

      setShowDialog(false);
      loadCountries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleToggleActive = async (country) => {
    try {
      const token = getAuthToken();
      await axios.patch(
        `${API}/admin/countries/${country.id}`,
        { is_active: !country.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(country.is_active ? 'Pays désactivé' : 'Pays activé');
      loadCountries();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (country) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${country.name} ?`)) return;

    try {
      const token = getAuthToken();
      await axios.delete(`${API}/admin/countries/${country.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pays supprimé avec succès');
      loadCountries();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Gestion Multi-Pays</h2>
          <p className="text-gray-600 mt-1">
            Gérez les pays, villes et districts de votre plateforme
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un pays
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Pays configurés ({countries.length})</CardTitle>
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Rechercher un pays..."
              className="max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredCountries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun pays trouvé</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Devise</TableHead>
                  <TableHead>Préfixe</TableHead>
                  <TableHead>Villes</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCountries.map((country) => (
                  <TableRow key={country.id}>
                    <TableCell className="font-mono font-semibold">{country.code}</TableCell>
                    <TableCell>{country.name}</TableCell>
                    <TableCell>{country.currency}</TableCell>
                    <TableCell>{country.phone_prefix}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {country.cities?.length || 0} ville(s)
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={country.is_active}
                        onCheckedChange={() => handleToggleActive(country)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(country)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(country)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCountry ? 'Modifier le pays' : 'Ajouter un pays'}
            </DialogTitle>
            <DialogDescription>
              Configurez les informations du pays et ses villes
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Country Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code pays (ISO) *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="CG, CD, FR"
                  maxLength={2}
                  disabled={editingCountry}
                />
              </div>
              <div>
                <Label>Nom du pays *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Congo-Brazzaville"
                />
              </div>
              <div>
                <Label>Devise *</Label>
                <Input
                  value={formData.currency}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))}
                  placeholder="FCFA, USD, EUR"
                />
              </div>
              <div>
                <Label>Préfixe téléphone *</Label>
                <Input
                  value={formData.phone_prefix}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_prefix: e.target.value }))}
                  placeholder="+242, +243"
                />
              </div>
            </div>

            {/* Cities */}
            <div>
              <Label className="text-lg font-semibold mb-3 block">Villes</Label>
              
              {/* Add City Form */}
              <Card className="mb-4">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      placeholder="Code (BZV)"
                      value={newCity.code}
                      onChange={(e) => setNewCity(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    />
                    <Input
                      placeholder="Nom (Brazzaville)"
                      value={newCity.name}
                      onChange={(e) => setNewCity(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <Button onClick={handleAddCity} className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </div>
                  <Input
                    className="mt-2"
                    placeholder="Districts/Arrondissements (séparés par des virgules)"
                    value={newCity.districts}
                    onChange={(e) => setNewCity(prev => ({ ...prev, districts: e.target.value }))}
                  />
                </CardContent>
              </Card>

              {/* Cities List */}
              {formData.cities.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Aucune ville ajoutée</p>
              ) : (
                <div className="space-y-2">
                  {formData.cities.map((city) => (
                    <Card key={city.code}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono font-semibold">{city.code}</span>
                              <span>-</span>
                              <span className="font-semibold">{city.name}</span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {city.districts.length} district(s): {city.districts.join(', ')}
                            </p>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveCity(city.code)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit}>
              {editingCountry ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CountryManagement;
